import { CurrencyPipe } from "@angular/common";
import {
  Component,
  computed,
  inject,
  input,
  model,
  signal,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { CampaignType, StrategyType } from "@front/m19-api-client";
import { CAMPAIGN_TYPE, StrategyService } from "@front/m19-injection-token";
import { CampaignTypeWithTacos, Marketplaces, SegmentEx, StrategyEx, StrategyGroupEx } from "@front/m19-models";
import {
  AccountSelectionService,
  AudiencesService,
  AuthService,
  CurrencyService,
  DspStrategyService,
  SegmentService,
  SpStrategyService,
} from "@front/m19-services";
import {
  IBadgeComponent,
  IButtonDirective,
  IErrorComponent,
  IFormFieldComponent,
  IIconComponent,
  IInputGroupDirective,
  IInputGroupInput,
  IInputGroupSuffixDirective,
  IToggleComponent,
  ITooltipDirective,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ToggleAutoSegmentComponent } from "@m19-board/shared/strategy-advanced-settings/toggle-auto-segment/toggle-auto-segment.component";
import { ToggleBusinessBoostComponent } from "@m19-board/shared/strategy-advanced-settings/toggle-business-boost/toggle-business-boost.component";
import { ToggleProductTargetingComponent } from "@m19-board/shared/strategy-advanced-settings/toggle-product-targeting/toggle-product-targeting.component";
import { DayPartingInputsComponent } from "@m19-board/strategies/day-parting-inputs/day-parting-inputs.component";
import { AdvancedSettings } from "@m19-board/strategies/strategy-page/strategy-page-advanced-settings/strategy-page-advanced-settings.component";
import { StrategyPageToggleAiPoweredTargetingComponent } from "@m19-board/strategies/strategy-page/strategy-page-toggle-ai-powered-targeting/strategy-page-toggle-ai-powered-targeting.component";
import { of, startWith, switchMap } from "rxjs";
import { InputModalComponent, InputModalData } from "../input-modal/input-modal.component";
import {
  SelectAudienceModalComponent,
  SelectAudienceModalData,
} from "./select-audience-modal/select-audience-modal.component";
import { ToggleBoostPlacementTopComponent } from "./toggle-boost-placement-top/toggle-boost-placement-top.component";
import { ToggleTosroComponent } from "./toggle-tosro/toggle-tosro.component";
import { SettingRowComponent } from "./setting-row/setting-row.component";

@Component({
  selector: "app-strategy-advanced-settings",
  providers: [CurrencyPipe],
  imports: [
    TranslocoDirective,
    IButtonDirective,
    IBadgeComponent,
    IToggleComponent,
    DayPartingInputsComponent,
    IInputGroupDirective,
    FormsModule,
    IInputGroupInput,
    ReactiveFormsModule,
    IFormFieldComponent,
    IErrorComponent,
    IInputGroupSuffixDirective,
    ToggleAutoSegmentComponent,
    StrategyPageToggleAiPoweredTargetingComponent,
    ToggleProductTargetingComponent,
    ToggleBusinessBoostComponent,
    IIconComponent,
    ITooltipDirective,
    ToggleBoostPlacementTopComponent,
    ToggleTosroComponent,
    SettingRowComponent,
  ],
  templateUrl: "./strategy-advanced-settings.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyAdvancedSettingsComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);
  private readonly strategyService = inject(StrategyService);
  private readonly dspStrategyService = inject(DspStrategyService);
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly toastrService = inject(ToasterService);
  private readonly currencyService = inject(CurrencyService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly segmentService = inject(SegmentService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly strategyAudiencesService = inject(AudiencesService);
  private readonly campaignType = inject(CAMPAIGN_TYPE);
  readonly CampaignType = CampaignType;
  readonly StrategyType = StrategyType;
  readonly MAX_FREQUENCY_CAP = DspStrategyService.MAX_FREQUENCY_CAP;

  readonly dailySpendModal = viewChild<TemplateRef<any>>("updateMinDailySpendModal");
  dailySpendModalRef!: ModalRef<any, TemplateRef<any>>;

  strategy = model.required<StrategyEx>();
  strategyGroup = input<StrategyGroupEx>();
  advancedSettings = input.required<Record<AdvancedSettings, boolean>>();

  accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, { requireSync: true });
  isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });
  locale = toSignal(this.authService.userLocale$);
  segmentIndex = toSignal<Map<number, SegmentEx>>(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
      startWith(new Map()),
    ),
    { requireSync: true },
  );
  organizationId = computed(() => this.accountMarketplace().resourceOrganizationId);
  currencyCode = computed(() => Marketplaces[this.accountMarketplace().marketplace].currency);

  audiences = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => {
        if (this.campaignType === CampaignTypeWithTacos.TACOS) return of([]);
        return this.strategyAudiencesService.getAudiences(
          am.accountId,
          am.marketplace,
          this.campaignType,
          am.dspAdvertiserId,
        );
      }),
      startWith([]),
    ),
    { requireSync: true },
  );

  dailyBudget = computed(() => this.strategy()?.dailyBudget);
  minDailySpendLimit = computed(() => this.accountMarketplace().minDailyBudgetLimit);
  maxMinDailySpend = computed(() =>
    this.dailyBudget()
      ? Math.floor(
          Math.min(
            this.minDailySpendLimit()! / this.currencyService.getCurrencyRate(this.currencyCode()),
            0.5 * this.dailyBudget()!,
          ),
        )
      : Math.floor(this.minDailySpendLimit()! / this.currencyService.getCurrencyRate(this.currencyCode())),
  );

  strategyLabelButtonLabel = computed(() =>
    this.strategy().strategyLabel !== undefined && this.strategy().strategyLabel !== ""
      ? this.strategy().strategyLabel
      : this.translocoService.translate("common.add"),
  );

  hasBrandStrategies = computed(
    () =>
      this.strategyGroup() &&
      this.strategyGroup()!.brandStrategies &&
      this.strategyGroup()!.brandStrategies!.length! > 0,
  );
  hasKeywordStrategies = computed(
    () =>
      this.strategyGroup() &&
      this.strategyGroup()!.keywordStrategies &&
      this.strategyGroup()!.keywordStrategies!.length! > 0,
  );

  minDailySpend = computed(
    () =>
      new FormControl(this.strategy().minDailySpend ?? null, {
        validators: [Validators.max(this.maxMinDailySpend())],
      }),
  );

  updateMinDailySpendLoading = signal(false);
  disableBoostAudienceLoading = signal(false);
  selectedBoostAudience = computed(() =>
    this.audiences().find((audience) => audience.audienceId === this.strategy().boostAudienceId),
  );
  frequencyCapControl = computed(() => {
    const strategy = this.strategy();
    if (!strategy.isDspStrategy()) return undefined;

    return new FormControl(strategy.frequencyCap, {
      validators: [Validators.min(1), Validators.max(this.MAX_FREQUENCY_CAP)],
      nonNullable: true,
    });
  });
  agencyFee = computed(() => {
    const strategy = this.strategy();
    if (!strategy.isDspStrategy()) return undefined;
    return strategy.agencyFee;
  });

  isUpdatingFrequencyCap = signal(false);
  updateBusinessBoostLoading = signal(false);

  openStrategyLabelModal() {
    const modalRef = this.modalService.openModal<InputModalData, string>(InputModalComponent, {
      modalTitle: this.translocoService.translate("advanced-settings-modal.strategy_label"),
      data: {
        initialValue: this.strategy().strategyLabel,
        maxLength: 80,
      },
    });

    modalRef.afterClosed().subscribe((strategyLabel) => {
      if (strategyLabel) {
        this.updateStrategyLabel(strategyLabel);
      }
    });
  }

  updateStrategyLabel(strategyLabel: string): void {
    if (!this.strategy()) return;
    this.strategyService
      .updateStrategyLabel(
        this.strategy()!.accountId,
        this.strategy()!.marketplace,
        this.organizationId()!,
        this.strategy()!.strategyId,
        strategyLabel,
      )
      .subscribe({
        next: (strategy: StrategyEx) => {
          this.strategy.set(strategy);
          this.toastrService.success(
            this.translocoService.translate("strategy-page.strategy_label_updated"),
            this.translocoService.translate("strategy-page.success"),
          );
        },
        error: (error) => {
          this.toastrService.error(error, this.translocoService.translate("common.error"));
        },
      });
  }

  openMinDailySpendModal() {
    this.dailySpendModalRef = this.modalService.openModal(this.dailySpendModal()!, {
      modalTitle: this.translocoService.translate("advanced-settings-modal.update_min_daily_spend"),
      size: "sm",
    });
  }

  updateMinDailySpend(minDailySpend: number): void {
    if (!this.strategy()) return;
    this.updateMinDailySpendLoading.set(true);
    this.strategyService
      .updateStrategyMinDailySpend(
        this.strategy()!.accountId,
        this.strategy()!.marketplace,
        this.organizationId()!,
        this.strategy()!.strategyId,
        minDailySpend,
      )
      .subscribe({
        next: (strategy: StrategyEx) => {
          this.strategy.set(strategy);
          this.toastrService.success(
            this.translocoService.translate("strategy-page.min_daily_spend_updated"),
            this.translocoService.translate("strategy-page.success"),
          );
          this.dailySpendModalRef.close();
          this.updateMinDailySpendLoading.set(false);
        },
        error: (error) => {
          this.toastrService.error(error, this.translocoService.translate("common.error"));
          this.dailySpendModalRef.close();
          this.updateMinDailySpendLoading.set(false);
        },
      });
  }

  updateFrequencyCap() {
    const frequencyCapControl = this.frequencyCapControl();

    if (!frequencyCapControl?.valid) return;
    this.isUpdatingFrequencyCap.set(true);
    this.dspStrategyService
      .updateDspStrategyFrequencyCap(this.strategy().strategyId, frequencyCapControl.value!)
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("advanced-settings-modal.frequency_cap_successfully_updated"),
          );
          this.isUpdatingFrequencyCap.set(false);
        },
        error: (_) => {
          this.toastrService.error(
            this.translocoService.translate("advanced-settings-modal.frequency_cap_update_error"),
          );
          this.isUpdatingFrequencyCap.set(false);
        },
      });
  }

  openSubStrategyCreationPage(strategyType: StrategyType) {
    this.router.navigate(
      [`/advertising/sponsored-product/strategy-group/${this.strategyGroup()!.strategyGroupId}/create-strategy`],
      {
        queryParamsHandling: "merge",
        queryParams: {
          strategyType,
        },
      },
    );
  }

  openBoostAudienceModal(checked: boolean) {
    if (!this.strategy()) return;
    if (checked) {
      const ref = this.modalService.openModal<SelectAudienceModalData, StrategyEx>(SelectAudienceModalComponent, {
        size: "sm",
        modalTitle: this.translocoService.translate("advanced-settings-modal.boost_audience"),
        data: {
          strategy: this.strategy(),
          audiences: this.audiences(),
          organizationId: this.organizationId()!,
        },
      });

      ref.afterClosed().subscribe((strategy: StrategyEx) => {
        if (strategy) {
          this.strategy.set(strategy);
        }
      });
    } else {
      this.disableBoostAudienceLoading.set(true);
      this.strategyService
        .updateStrategyBoostAudience(
          this.accountMarketplace().accountId,
          this.accountMarketplace().marketplace,
          this.strategy().strategyId,
          this.organizationId()!,
          undefined,
        )
        .subscribe({
          next: (strategy: StrategyEx) => {
            this.strategy.set(strategy);
            this.disableBoostAudienceLoading.set(false);
          },
          error: (error) => {
            this.toastrService.error(error?.[0], this.translocoService.translate("common.error"));
            this.disableBoostAudienceLoading.set(false);
          },
        });
    }
  }
}
